/**
 * VoiceContext — owns the embed's voice session at App level so the
 * LiveKit room survives view switches (Chat → Trip → Bookings, etc.).
 *
 * Wraps `useEmbedAudioChat` (the 1:1 port of wunderguide-web's voice
 * hook) and re-exposes its state + start/stop. Mounted once in App.tsx
 * inside InstanceProvider; consumers read via `useVoice()`.
 */
import React, { createContext, useContext, useMemo } from 'react';
import {
  useEmbedAudioChat,
  type UseEmbedAudioChat,
} from '../hooks/useEmbedAudioChat';

type VoiceContextValue = UseEmbedAudioChat;

const VoiceContext = createContext<VoiceContextValue | undefined>(undefined);

export const VoiceProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const audioChat = useEmbedAudioChat();
  const value = useMemo<VoiceContextValue>(
    () => audioChat,
    // Deps cover the actual state slots the hook exposes.
    [
      audioChat.isListening,
      audioChat.isConnecting,
      audioChat.hasRemoteParticipant,
      audioChat.micAudioLevel,
      audioChat.botAudioLevel,
      audioChat.isBotWorking,
      audioChat.permissionState,
      audioChat.toggleListening,
      audioChat.startListening,
      audioChat.stopListening,
      audioChat.voiceContentBatches,
    ],
  );
  return (
    <VoiceContext.Provider value={value}>{children}</VoiceContext.Provider>
  );
};

export function useVoice(): VoiceContextValue {
  const ctx = useContext(VoiceContext);
  if (!ctx) throw new Error('useVoice must be used within VoiceProvider');
  return ctx;
}
